import { useState, useEffect, useCallback, useRef } from 'react';

const POSITION_STORAGE_KEY = 'seller-assistant-panel-position';

const PANEL_WIDTH = 420;
const PANEL_WIDTH_WIDE = 560;
const PANEL_HEIGHT = 600;
const PANEL_HEIGHT_MIN = 64;
const VIEWPORT_MARGIN = 16;

function getDefaultPosition(width = PANEL_WIDTH, height = PANEL_HEIGHT) {
  if (typeof window === 'undefined') {
    return { x: VIEWPORT_MARGIN, y: VIEWPORT_MARGIN };
  }
  return {
    x: Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
    y: Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN),
  };
}

function loadStoredPosition() {
  try {
    const raw = sessionStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function clampPosition(x, y, width, height) {
  if (typeof window === 'undefined') return { x, y };
  const maxX = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
  const maxY = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
  return {
    x: Math.min(Math.max(VIEWPORT_MARGIN, x), maxX),
    y: Math.min(Math.max(VIEWPORT_MARGIN, y), maxY),
  };
}

export function useDraggablePanel({ enabled, width, height }) {
  const [position, setPosition] = useState(() => {
    const stored = loadStoredPosition();
    return clampPosition(
      stored?.x ?? getDefaultPosition(width, height).x,
      stored?.y ?? getDefaultPosition(width, height).y,
      width,
      height
    );
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const persistPosition = useCallback((next) => {
    try {
      sessionStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setPosition((prev) => {
      const next = clampPosition(prev.x, prev.y, width, height);
      persistPosition(next);
      return next;
    });
  }, [width, height, persistPosition]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        const next = clampPosition(prev.x, prev.y, width, height);
        persistPosition(next);
        return next;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width, height, persistPosition]);

  const handleDragStart = useCallback(
    (event) => {
      if (!enabled) return;
      if (event.button !== 0) return;
      if (event.target.closest('button')) return;

      event.preventDefault();
      setIsDragging(true);
      dragOffset.current = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
    },
    [enabled, position.x, position.y]
  );

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (event) => {
      const next = clampPosition(
        event.clientX - dragOffset.current.x,
        event.clientY - dragOffset.current.y,
        width,
        height
      );
      setPosition(next);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setPosition((prev) => {
        persistPosition(prev);
        return prev;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, width, height, persistPosition]);

  return {
    position,
    isDragging,
    handleDragStart,
  };
}

export { PANEL_WIDTH, PANEL_WIDTH_WIDE, PANEL_HEIGHT, PANEL_HEIGHT_MIN };
